"use client";

import Link from "next/link";
import { Star } from "lucide-react";

import { PricingLink } from "@/components/platform/PricingLink";
import { usePlatformAuth } from "@/hooks/use-platform-auth";
import { usePricingDestination } from "@/hooks/use-pricing-destination";
import { PRICING_PLAN_FEATURES } from "@/lib/billing/pricing-plan-features";
import { WORKSPACE_HOME_HREF } from "@/lib/platform/account-nav";
import type { ResolvedPublicPricing } from "@/lib/pricing/types";
import type { DisplayCurrency } from "@/lib/pricing/config";
import { cn } from "@/lib/utils";

function Check({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={className}
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        backgroundColor: "currentColor",
        WebkitMaskImage:
          'url("/Static Icons/icons8-tick-material-rounded/icons8-tick-24.svg")',
        maskImage:
          'url("/Static Icons/icons8-tick-material-rounded/icons8-tick-24.svg")',
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
}

type MarketingPricingPlansProps = {
  pricing: ResolvedPublicPricing;
  compact?: boolean;
};

export function MarketingPricingPlans({
  pricing,
  compact = false,
}: MarketingPricingPlansProps) {
  const currency = pricing.currency as DisplayCurrency;
  const { isAuthenticated } = usePlatformAuth();
  const pricingDestination = usePricingDestination();
  const freeCtaHref = isAuthenticated ? WORKSPACE_HOME_HREF : "/signup";
  const freeCtaLabel = isAuthenticated ? "Open workspace" : "Get started";
  const proCtaLabel = isAuthenticated
    ? pricingDestination.isCurrent
      ? "Viewing plans"
      : "View plans"
    : "Get started";
  const proCtaClassName =
    "relative mt-8 flex h-12 w-full items-center justify-center rounded-xl bg-indigo-600 text-[13px] font-semibold text-white shadow-[0_8px_24px_-8px_rgba(79,70,229,0.55)] transition hover:bg-indigo-500 active:scale-[0.98]";

  return (
    <div className={cn("w-full", compact ? "" : "mx-auto max-w-[900px]")}>
      <div className="grid gap-6 md:grid-cols-2 md:items-stretch md:gap-8">
        <div className="flex min-h-[520px] flex-col rounded-[24px] border border-white/[0.08] bg-white/[0.02] p-8 backdrop-blur-sm">
          <div className="mb-3 mt-1">
            <h2 className="text-lg font-semibold text-white">Free</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-neutral-400">
              For personal use and getting started.
            </p>
          </div>

          <div className="mb-6 mt-4">
            <div className="flex items-baseline gap-1">
              <span className="text-[14px] font-medium text-white">
                {pricing.currencySymbol}
              </span>
              <span className="text-[36px] font-semibold leading-none text-white">
                0
              </span>
              <span className="text-[12px] text-neutral-500">/mo</span>
            </div>
          </div>

          <ul className="flex flex-1 flex-col gap-3 border-t border-white/[0.06] pt-8">
            {PRICING_PLAN_FEATURES.free.map((feature) => (
              <li
                key={feature.name}
                className="flex items-start gap-2.5 text-[13px] text-neutral-400"
              >
                <Check className="mt-0.5 text-emerald-500/80" />
                <span>{feature.name}</span>
              </li>
            ))}
          </ul>

          <Link
            href={freeCtaHref}
            className="mt-8 flex h-12 w-full items-center justify-center rounded-xl border border-white/10 text-[13px] font-medium text-white transition hover:border-white/20 hover:bg-white/[0.04]"
          >
            {freeCtaLabel}
          </Link>
        </div>

        <div className="relative flex min-h-[520px] flex-col overflow-visible rounded-[24px] border border-indigo-500/25 bg-gradient-to-br from-indigo-950/50 to-[#0a0a12] p-8 pt-10">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-indigo-500/15 blur-3xl"
          />
          <div className="absolute left-1/2 top-0 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-amber-400/30 bg-gradient-to-r from-amber-500/90 to-orange-500/90 px-3.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
            <Star size={11} className="fill-white" strokeWidth={3} />
            <span>Pro</span>
          </div>

          <div className="relative mb-3 mt-2">
            <h2 className="text-lg font-semibold text-white">Monthly</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-neutral-400">
              For creators who want the full intelligence layer.
            </p>
          </div>

          <div className="relative mb-6 mt-4">
            <div className="flex items-baseline gap-1">
              <span className="text-[14px] font-medium text-white">
                {pricing.currencySymbol}
              </span>
              <span className="text-[36px] font-semibold leading-none text-white">
                {pricing.amount}
              </span>
              <span className="text-[12px] text-neutral-500">/mo</span>
            </div>
          </div>

          <ul className="relative flex flex-1 flex-col gap-3 border-t border-white/[0.06] pt-8">
            {PRICING_PLAN_FEATURES.pro.map((feature) => (
              <li
                key={feature.name}
                className="flex items-start gap-2.5 text-[13px] text-neutral-300"
              >
                <Check className="mt-0.5 text-indigo-400" />
                <span>{feature.name}</span>
              </li>
            ))}
          </ul>

          {isAuthenticated ? (
            pricingDestination.isCurrent ? (
              <span
                className={cn(proCtaClassName, "cursor-default opacity-80")}
                aria-current="page"
              >
                {proCtaLabel}
              </span>
            ) : (
              <PricingLink className={proCtaClassName}>{proCtaLabel}</PricingLink>
            )
          ) : (
            <Link href="/signup" className={proCtaClassName}>
              {proCtaLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
